/* import __COLOCATED_TEMPLATE__ from './signup-service-suggestion-selector.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { SERVICES_DESCRIPTIONS } from 'sh-www/constants/services';

export const mapServiceToOptions = (service) => {
  const { name: key, category } = service.getProperties(['name', 'category']);
  const props = { key, service };

  if (category) {
    props.category = category;
  }

  const tooltip = SERVICES_DESCRIPTIONS[key]?.description;
  if (tooltip) {
    props.tooltip = tooltip;
  }

  return props;
};

const LOGGING_DEBOUNCE_MS = 400;

class SignupServiceSuggestionSelectorComponent extends Component {
  @service('error-tracker') errorTracker;
  @service('commands.service-commands') serviceCommands;

  @tracked internalValue = '';
  @tracked searchInputHistory = [];

  get mappedOptions() {
    return this.args.options.map(mapServiceToOptions);
  }

  get selectedOptions() {
    return this.args.selected.map(mapServiceToOptions);
  }

  get synonyms() {
    return this.args.options.reduce((accum, option) => accum.concat(option.serviceSynonyms?.toArray() ?? []), []);
  }

  #filteredOptionsCache = createCache(() => {
    const internalValue = this.internalValue;
    const mappedOptions = this.mappedOptions;

    if (!internalValue) {
      return mappedOptions;
    }

    return mappedOptions.filter(({ key }) => {
      const pattern = new RegExp(`(\\b${internalValue})`, 'ig');
      return pattern.test(key);
    });
  });

  get filteredOptions() {
    return getValue(this.#filteredOptionsCache);
  }

  get suggestions() {
    const synonyms = this.synonyms;

    if (!synonyms.length || !this.internalValue) {
      return [];
    }

    const pattern = new RegExp(`(\\b${this.internalValue})`, 'i');
    const matches = synonyms.filter(({ name }) => {
      return pattern.test(name);
    });
    
    const visibleOptions = new Map(this.filteredOptions.map(option => [option.key, true]));

    return matches.reduce((suggestions, synonym) => {
      const matchedServiceName = synonym.service.name;
      if (!visibleOptions.has(matchedServiceName)) {
        const suggestion = {
          key: matchedServiceName,
          service: synonym.service,
          category: 'Suggestions'
        };
  
        const tooltip = SERVICES_DESCRIPTIONS[matchedServiceName]?.description
        if (tooltip) {
          suggestion.tooltip = tooltip;
        }
        visibleOptions.set(matchedServiceName, true)
        suggestions.push(suggestion);
      }

      return suggestions;
    }, []);
  }

  get options() {
    return this.filteredOptions.concat(this.suggestions);
  }

  @restartableTask
  *logSearchString(value) {
    yield timeout(LOGGING_DEBOUNCE_MS);

    if (value) {
      const inputHistory = [...this.searchInputHistory];
      if (inputHistory.length >= 20) {
        inputHistory.shift();
      }
      inputHistory.push(value);
      this.searchInputHistory = inputHistory;
    }
  }

  @task
  *storeSearchHistory(service, searchHistory) {
    try {
      yield this.serviceCommands.recordSearchInput(service, searchHistory.toString());
    } catch (e) {
      this.errorTracker.error('There was a problem storing service search input history', this, e);
    }
  }

  @action
  handleBlur() {
    this.internalValue = '';
    this.args.onblur?.();
  }

  @action
  handleInput(value) {
    this.internalValue = value;
    this.logSearchString.perform(value);
  }

  @action
  handleAdded({ service }) {
    this.storeSearchHistory.perform(service, this.searchInputHistory);
    this.internalValue = '';
    this.searchInputHistory = [];

    const index = this.args.selected.findIndex((s) => {
      return service.id === s.id;
    });

    if (index === -1) {
      this.args.onadded?.(service);
    }
  }

  @action
  handleRemoved({ service }) {
    this.args.onremoved?.(service);
  }
}

export default SignupServiceSuggestionSelectorComponent;
